import type { ScheduleDetail, ScheduleMutationResult } from '../types'

/**
 * Folds a `ScheduleMutationResult` into the cached `ScheduleDetail`: replaces/inserts changed
 * activities and dependencies by id, drops deleted ones, keeps external ghosts in sync with
 * foreign rows the mutation touched, and swaps in the fresh `schedule`. Pure, no Apollo.
 */
export const applyMutationResult = (detail: ScheduleDetail, result: ScheduleMutationResult): ScheduleDetail => {
  const deletedA = new Set(result.deletedActivityIds)
  const changed = new Map(result.changedActivities.map((a) => [a.id, a]))
  const activities = detail.activities.filter((a) => !deletedA.has(a.id)).map((a) => changed.get(a.id) ?? a)
  const known = new Set(activities.map((a) => a.id))
  for (const a of result.changedActivities) if (a.scheduleId === detail.schedule.id && !known.has(a.id) && !deletedA.has(a.id)) activities.push(a)

  const deletedD = new Set(result.deletedDependencyIds)
  const changedD = new Map(result.dependencies.map((d) => [d.id, d]))
  const alive = new Set(activities.map((a) => a.id))
  const dependencies = detail.dependencies
    .filter((d) => !deletedD.has(d.id) && !(deletedA.has(d.predecessorId) || deletedA.has(d.successorId)))
    .map((d) => changedD.get(d.id) ?? d)
  const knownD = new Set(dependencies.map((d) => d.id))
  for (const d of result.dependencies) if (!knownD.has(d.id) && !deletedD.has(d.id) && (alive.has(d.predecessorId) || alive.has(d.successorId))) dependencies.push(d)

  const externalActivities = detail.externalActivities.map((e) => {
    const c = changed.get(e.id)
    return c ? { ...e, name: c.name, wbsCode: c.wbsCode, start: c.start, finish: c.finish, isMilestone: c.isMilestone } : e
  })
  return { ...detail, schedule: result.schedule, activities, dependencies, externalActivities }
}
